/**
 * Copyright © 2026 Delicious AI, LLC
 */

import { useQuery } from '@apollo/client'
import {
  Box,
  Chip,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Toolbar,
  Typography,
} from '@mui/material'
import { useEffect, useState } from 'react'

import {
  GET_DISPLAY_PLANOGRAM_PERFORMANCE_HISTORY,
  GetDisplayPlanogramPerformanceHistoryQuery,
  GetDisplayPlanogramPerformanceHistoryQueryVariables,
  PlanogramChangeDetailsFragment,
} from '@/graphql'
import useT from '@/i18n/useT'
import PlanogramHistoryDialog from '@/pages/planogram/components/PlanogramHistoryDialog'

const formatCurrency = (value: number | null) =>
  value === null
    ? '—'
    : new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: 'USD',
      }).format(value)

export default function PlanogramPerformanceHistory({
  displayId,
}: {
  displayId: string
}) {
  const { t } = useT()
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(3)
  const [selectedChange, setSelectedChange] =
    useState<PlanogramChangeDetailsFragment | null>(null)
  const { data, loading, error } = useQuery<
    GetDisplayPlanogramPerformanceHistoryQuery,
    GetDisplayPlanogramPerformanceHistoryQueryVariables
  >(GET_DISPLAY_PLANOGRAM_PERFORMANCE_HISTORY, {
    variables: {
      displayId,
      pagination: {
        limit: rowsPerPage,
        offset: page * rowsPerPage,
      },
    },
    fetchPolicy: 'cache-and-network',
  })
  const rows = data?.displayPlanogramPerformanceHistory ?? []

  useEffect(() => {
    setPage(0)
    setSelectedChange(null)
  }, [displayId])

  return (
    <Box>
      <Paper variant="outlined">
        <Toolbar sx={{ pl: 2, pr: 1 }}>
          <Typography variant="h6">
            {t(
              'planogramPerformanceHistory.title',
              'Planogram performance history',
            )}
          </Typography>
        </Toolbar>
        {loading && !data ? (
          <Box sx={{ px: 2, pb: 2 }}>
            <CircularProgress size={24} />
          </Box>
        ) : error ? (
          <Typography color="error" sx={{ px: 2, pb: 2 }}>
            {error.message}
          </Typography>
        ) : rows.length === 0 ? (
          <Typography color="text.secondary" sx={{ px: 2, pb: 2 }}>
            {t(
              'planogramPerformanceHistory.empty',
              'No planogram changes yet.',
            )}
          </Typography>
        ) : (
          <>
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table size="small" sx={{ minWidth: 780 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>
                      {t(
                        'planogramPerformanceHistory.columns.changed',
                        'Planogram changed',
                      )}
                    </TableCell>
                    <TableCell>
                      {t(
                        'planogramPerformanceHistory.columns.status',
                        'Status',
                      )}
                    </TableCell>
                    <TableCell>
                      {t(
                        'planogramPerformanceHistory.columns.period',
                        'Performance period',
                      )}
                    </TableCell>
                    <TableCell align="right">
                      {t('planogramPerformanceHistory.columns.days', 'Days')}
                    </TableCell>
                    <TableCell align="right">
                      {t(
                        'planogramPerformanceHistory.columns.averageDailyRevenue',
                        'Avg. daily revenue',
                      )}
                    </TableCell>
                    <TableCell align="right">
                      {t(
                        'planogramPerformanceHistory.columns.totalRevenue',
                        'Total revenue',
                      )}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((entry, index) => {
                    const isLatest = page * rowsPerPage + index === 0
                    const superseded = !entry.change.completedAt && !isLatest
                    const status = entry.change.completedAt
                      ? t('planogramHistory.status.completed', 'Completed')
                      : superseded
                        ? t('planogramHistory.status.superseded', 'Superseded')
                        : t(
                            'planogramPerformanceHistory.status.pendingRestock',
                            'Pending restock',
                          )
                    return (
                      <TableRow
                        hover
                        key={entry.change.id}
                        onClick={() => setSelectedChange(entry.change)}
                        sx={{ cursor: 'pointer' }}
                      >
                        <TableCell>
                          {new Date(entry.change.created).toLocaleString()}
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={status}
                            color={
                              entry.change.completedAt
                                ? 'success'
                                : superseded
                                  ? 'warning'
                                  : 'default'
                            }
                          />
                        </TableCell>
                        <TableCell>
                          {entry.performanceStartAt
                            ? `${new Date(
                                entry.performanceStartAt,
                              ).toLocaleDateString()} – ${
                                entry.performanceEndAt
                                  ? new Date(
                                      entry.performanceEndAt,
                                    ).toLocaleDateString()
                                  : t(
                                      'planogramPerformanceHistory.period.now',
                                      'Now',
                                    )
                              }`
                            : '—'}
                        </TableCell>
                        <TableCell align="right">
                          {entry.measuredDays ?? '—'}
                        </TableCell>
                        <TableCell align="right">
                          {formatCurrency(entry.averageDailyRevenue)}
                        </TableCell>
                        <TableCell align="right">
                          {formatCurrency(entry.totalRevenue)}
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={data?.displayPlanogramHistoryCount ?? 0}
              page={page}
              rowsPerPage={rowsPerPage}
              rowsPerPageOptions={[3, 5, 10, 25]}
              onPageChange={(_, nextPage) => setPage(nextPage)}
              onRowsPerPageChange={event => {
                setRowsPerPage(Number(event.target.value))
                setPage(0)
              }}
            />
          </>
        )}
      </Paper>
      <PlanogramHistoryDialog
        change={selectedChange}
        onClose={() => setSelectedChange(null)}
      />
    </Box>
  )
}
